import {
  CORP_DISRUPTION_PROTECTION_BODY,
  CORP_DISRUPTION_REBOOKING_BODY,
  FLIGHT_CANCEL_FOR_ANY_REASON_BODY,
  HOTEL_CFAR_BODY,
  HOTEL_CFAR_TABLE,
  HOTEL_CFAR_TITLE,
  WEBSITE_TERMS_OF_USE_BODY,
} from "redmond";
import { CORP_TRAVEL_CREDITS_BODY } from "./travelCreditsTextConstant";

export const TERMS_OF_SERVICE_TITLE =
  "Capital One Business Travel Terms and Conditions";
export const TERMS_OF_SERVICE_SUBTITLE = "";

export const TERMS_OF_SERVICE_BODY = (offerAmount: number = 50) => `
<p>
  Thank you for using the Capital One Business Travel website and mobile properties. The term
  "Capital One" in these Terms and Conditions means Capital One, N.A and Capital One Travel LLC
  (collectively). The terms “Capital One Business Travel or "we" or "us" or "our" in these Terms
  and Conditions means Capital One Business Travel which is a service for Capital One, N.A., with
  offices at 1680 Capital One Drive, McLean, Virginia. Capital One Business Travel provides the
  materials contained on this website and mobile properties on the World Wide Web, which hereinafter
  will be referred to as the “Sites,” as a service to our customers and visitors. Capital One Business Travel requires that all
  visitors to the Sites adhere to the following Terms and Conditions. By accessing the Sites and any
  of its pages, you indicate your acknowledgement and acceptance of the Terms and Conditions set
  forth below without limitation or qualification.
</p>
<p>
    The term “Non-Financial Users” in these Terms and Conditions refers to users that are 1) invited to create a Capital One Business Travel-specific login tied to an eligible Capital One Business card account and 2) not digitally-enrolled users with a broader Capital One login on the business card account. The term “Admin” refers to users designated with the travel administrator role on Capital One Business Travel with administrative privileges . While primary and newly added account manager cardholders are Admins by default, those cardholders can also assign administrative privileges to other digitally enrolled users on their business card account.
</p>
<p>
  From time to time, we may change these terms. We recommend that you review them on a regular
  basis.
</p>
<p>
  Some of the services made available through Capital One Business Travel are offered by Hopper
  (USA) Inc. and its affiliated companies (“Hopper”). Your use of the services offered by Hopper and
  other third-parties that may be linked to from the Sites will be governed by their respective
  Terms and Conditions and Privacy Policies.
</p>
<h3 id="general-booking-terms-and-condition"><i>General Booking Terms and Condition</i></h3>
<ul>
  <li>
    <strong>Payment methods. </strong>Customers with a US-issued, eligible Capital One business card
    can book travel reservations for flights, stays and car rentals on the Sites and may pay via
    reward redemptions, travel credits, and/or an eligible Capital One business card. Reward
    redemptions that are included as part of your transaction will be deducted from your reward
    program account. Travel credit usages that are included as part of your transaction will be
    deducted. Payments by credit card will appear on your monthly card statement as “COT*[Trip
    Component]*BIZ*[TripID]*”. You may view the specific airline, hotel, car rental or vacation rental supplier you
    booked with (each, a "Supplier" and collectively, "Suppliers") on the Sites under My trips. Non-Financial Users added to Capital One Business Travel may use any valid debit or credit card that they are authorized to make purchases on.
  </li>
  <li>
    <strong>Payment issues and fraud. </strong>Capital One Business Travel may cancel the
    booking in the event of non-payment, payment dispute, rewards dispute, fraud, or suspected fraud
    and penalties imposed by Suppliers, up to the full amount of each ticket/reservation, may be
    applied.
  </li>
  <li>
    <strong>Rules and regulations. </strong>All bookings made through Capital One Business Travel
    shall comply with all applicable governmental laws, rules and regulations including,
    without limitation, sanctions issued by the Office of Foreign Assets Control. Such sanctions may
    prevent Capital One Business Travel from offering travel to specific destinations or
    individuals. To assist Capital One Business Travel with its compliance with laws, rules and
    regulations, Capital One Business Travel may ask you for additional information. Any booking
    made, or in good faith believed to be made, in violation of applicable law, will be canceled by
    Capital One Business Travel, in our sole discretion, with no liability to you other than to
    issue a refund, if permitted by law.
  </li>
  <li>
    <strong>Cancellations and changes.</strong> Reservations are subject to the rules of each
    Supplier on your itinerary; some reservations cannot be canceled for a refund. Itinerary changes
    are subject to Capital One Business Travel or Supplier imposed cancellation or change fees
    plus applicable fare differential. Refunds, if permitted, may take up to 90 days to process and
    are subject to the policies of the individual Supplier. All refund requests must be submitted
    within 30 days after the scheduled departure date and any negotiable documents (i.e. airline
    tickets, redeemable certificates or vouchers, etc.) issued must be returned to Capital One
    Business Travel prior to processing any refund. Refunds will be returned via original
    payment method, with split-pay transactions using both credit card and rewards or Capital One
    statement credits being refunded proportionally. Please note: The cancellation policy for
    reservations made through Capital One Business Travel may differ from the cancellation policy
    for reservations made directly with the Supplier.
  </li>
  <li>
    <strong>Phone booking fee. </strong>If you book via phone, you may be subject to a $25 phone
    booking fee for each flight itinerary you book through the phone. Our travel offerings are
    optimized for digital self-servicing, and we strongly encourage you to book online for the best
    experience. If you cannot book digitally due to needs for accessibility accommodations, language
    barriers, or web issues, or you are experiencing certain emergency situations, the phone booking
    fee may be waived.
  </li>
</ul>
<h3 id="who-can-use-capital-one-business-travel"><i>Who Can Use Capital One Business Travel</i></h3>
<p>
  Capital One business cardholders and their invited Non-financial users have access to Capital One Business Travel. The primary and account manager cardholders, or another cardholder on the account delegated by them as an Admin, may set up the business’s Capital One Business Travel policies and users. All active Capital One business card accounts automatically include access to Capital One Business Travel.
</p>
<p>
  By using Capital One Business Travel, you represent and warrant that any profile information you submit is true and accurate, that you are 18 years of age or older and fully able and competent to enter into and abide by these Terms and Conditions, and are either 1) an eligible Capital One business card holder, and are digitally enrolled (i.e., have an online login to a Capital One account) or 2) a Non-Financial User invited by an Admin to create a Capital One Business Travel-specific login.
</p>
<p>
  The features on Capital One Business Travel (a.k.a., the business travel platform) may differ
  from those available on the consumer travel booking website (a.k.a., Capital One Travel).
</p>
<p>
  If you are a cardholder on a business card account enrolled in Capital One Business Travel:
</p>
<ul>
  <li>
    You will be able to book travel on the business travel platform with your business card,
    provided that you have not been deactivated by an Admin.
  </li>
  <li>
    You will be able to book travel on the consumer travel booking website (a.k.a., Capital One
    Travel) if and only if you have a separate Capital One card that has access to the consumer
    travel booking website.
  </li>
</ul>
<p>
    If you are a Non-Financial User authorized by an Admin to book travel on the business platform, you may use any valid credit card and the terms of purchase and payment will be governed by the terms and conditions of that credit card.  You will not be able to book consumer travel on Capital One Travel if you do not have a separate Capital One card that has access to the consumer travel booking website.
</p>
<p>
  Except as otherwise expressly provided in these Terms and Conditions, you shall not utilize
  Capital One Business Travel to provide travel to non-employees or consumers (e.g. utilizing
  Capital One Business Travel in connection with your business as a travel agency). You may only
  use Capital One Business Travel to make legitimate reservations or purchases for you or other
  persons for whom you are authorized to act. You represent and warrant that you are not using
  Capital One Business Travel on behalf of any other entity, or for any commercial purpose. You
  shall not use Capital One Business Travel to make any speculative, false, fictitious, or
  fraudulent reservations.
</p>
<p>
  We reserve the right to refuse access to Capital One Business Travel or to limit or take away
  your access at any time and may take all available legal recourse for actual or suspected
  violations of these Terms and Conditions, including cancellation of bookings and closure of your
  Capital One card account. Any delay by Capital One Business Travel in taking such actions does
  not constitute a waiver of Capital One Business Travel’s rights to enforce these Terms and
  Conditions. By making Capital One Business Travel and the Sites available for your use,
  Capital One Business Travel does not consent to act as your agent or fiduciary. Use of our
  services is void where prohibited.
</p>
<p>
  As a condition of your use of the Sites, you warrant that you will not use the Sites for any
  purpose that is unlawful or prohibited by these Terms and Conditions.
</p>
<h3 id="earning-rewards-and-credits"><i>Earning Rewards and Credits</i></h3>
<p>
  When you book with an eligible Capital One business card offering elevated rewards on Capital One
  Business Travel purchases, your business’s primary cardholder will earn elevated rewards as
  detailed below. If you select the option to ‘pay at stay’ when booking a Premier or Lifestyle Collection
  hotel on Capital One Business Travel, you will not earn the elevated rewards, but will receive the
  standard purchase earn rate of the card you use to pay at the hotel.
</p>
<ul>
  <li>
    <strong>Venture X Business:</strong> 10 miles per dollar for hotel and rental car purchases, and 5 miles per dollar for flight and vacation rental purchases.
  </li>
  <li>
    <strong>Spark Miles, Venture Business, Spark Miles Select, VentureOne Business, Spark Classic Miles: </strong>5 miles per dollar for hotel, rental car and vacation rental purchases
  </li>
  <li>
    <strong
      >Spark Cash, Spark Cash Plus, Spark Cash Preferred, Spark Pro, Spark Pro charge card, Spark Cash Select, Spark Classic</strong
    ><strong>:</strong> 5% cash back on hotel, rental car and vacation rental purchases
  </li>
</ul>
<p>
  Not all Capital One-issued credit cards offer elevated rewards for purchases made
  through Capital One Business Travel. Earnings will apply to net purchases (purchases minus any
  credits or returns) only. Purchases made outside of the Capital One Business Travel booking
  sites, including but not limited to hotel incidentals, upgrades, or other expenses, will not earn
  the elevated rewards, but will receive the standard purchase earn rate of the credit card used for the purchase.
  Please note, only the primary cardholder will have access to view or redeem business rewards.
  Elevated rewards may differ, subject to promotional campaigns.
</p>
<p>
    When you are a Non-Financial User booking on Capital One Business Travel and paying with a Capital One consumer credit card eligible for rewards, you will earn rewards as detailed here.
</p>
<ul>
  <li>
    <strong>Venture X: </strong>10 miles per dollar for hotel and rental car purchases, and 5 miles per dollar for flight and vacation rental purchases.
  </li>
  <li>
    <strong>Venture, Venture One: </strong>5 miles per dollar for hotel, rental car and vacation rental purchases
  </li>
  <li>
    <strong
      >Savor, SavorOne, SavorOne Student, SavorOne for Good Credit</strong><strong>:</strong> 5% cash back on hotel, rental car and vacation rental purchases
  </li>
  <li>
    <strong>Quicksilver, QuicksilverOne, Quicksilver Student, Quicksilver Secured, Quicksilver for Good Credit: </strong>5% cash back on hotel, rental car and vacation rental purchases
  </li>
  <li>
    <strong>Journey: </strong>5% cash back on hotel, rental car and vacation rental purchases
  </li>
</ul>
<p>
    Not all Capital One-issued credit cards offer elevated rewards for purchases made through Capital One Business Travel. Purchases made outside of the Capital One Business Travel booking sites, including but not limited to hotel incidentals, upgrades, or other expenses, will not earn the elevated rewards, but will receive the card’s standard purchase earn rate. Elevated rewards may differ, subject to promotional campaigns.
</p>
<p>
    Capital One is not responsible for card rewards for purchases made with a non-Capital One credit or debit card on Capital One Business Travel.
</p>
<h3 id="managing-business-travel"><i>Managing Business Travel</i></h3>
<ul>
  <li>
    <strong>Travel management capabilities. </strong>Beyond booking travel, Capital One Business Travel
    empowers business cardholders to manage and analyze employees’ business travel spend.
    Capital One reserves the right to change and improve travel management capabilities at any time.
  </li>
</ul>
<ul>
  <li>
    <strong>Users.</strong> The Users tab allows Admins to edit an employee’s travel role and/or
    policy tier. Admins may deactivate users, which would prevent the user from accessing Capital
    One Business Travel. Admins may also reactivate users to re-establish that user’s access to
    the business travel platform. The Users tab is automatically populated with digitally-enrolled
    cardholders associated with the business card account, and allows Admins to invite employees as Non-Financial Users on a given Capital One Business Travel account.
  </li>
  <li>
    <strong>Policies. </strong>The Policies tab allows Admins to set maximum price caps for
    in-policy bookings, across flights, stays, and rental cars. Admins can set policies for the
    default “associate” policy tier, and also have the option to set separate caps for an
    “executive” policy tier.
  </li>
  <li>
    <strong>Out of Policy.</strong> The Out of Policy tab is where Admins can review all
    out-of-policy bookings. Admins will be able to see any business justification Travelers provide,
    and can call 844-422-6922 to cancel bookings for at least 24 hours after the original booking
    was made. If the out-of-policy booking is canceled, travelers will automatically get notified by
    email..
  </li>
  <li>
    <strong>All Trips. </strong>The All Trips tab will show all business travel booked on the
    business card account, across all travelers. Admins will be able to sort and search by trip
    date, booking type, airlines, stays names, rental car companies, traveler name, and trip status.
    Trip status filters will allow Admins to see which trips are happening at the moment, versus
    future trips and past trips.
  </li>
  <li>
    <strong>Reports. </strong>The Reports tab will allow Admins to view and analyze spend across
    Capital One Business Travel. Reports include a dashboard view of the number of trips taken,
    total spend across flights, stays, and rental cars, and travel spend insights. Reports are
    subject to change. Admins can download a CSV file with travel transaction data from the Reports
    tab.
  </li>
</ul>
<ul>
  <li>
    <strong>Travel roles. </strong>Travel roles govern permissions on Capital One Business Travel
    and are separate and distinct from cardholder roles (e.g., primary cardholder,
    Authorized User, and Account Manager). Travel roles and permissions are subject to change as we
    build out new features and capabilities, so please refer to the Capital One Business Travel
    Users tab for the latest and most accurate information.
    <ul>
        <li>
            <strong>Admin role. </strong>Admins will be able to view Reports, All Trips, Users, Policies, and Out of Policy bookings. Admins will be able to make edits to Users and Policies, and review out-of-policy bookings. By default, the primary cardholder will be an Admin and cannot be removed as an Admin. Newly added account managers are also Admins by default but can be removed as an Admin by the primary.
        </li>
        <li>
            <strong>Traveler role. </strong>Travelers will be able to book in-policy and out-of-policy
            travel, and digitally self-service their travel (e.g., change or cancel reservations) subject to
            the rules of each Supplier on their itinerary.
        </li>
    </ul>
  </li>
</ul>
<ul>
  <li>
    <strong>User types. </strong>There are different types of Capital One Business Travel users:
    <ul>
      <li>
        <strong>A user that has travel and business card account access. </strong>This type of user is an authorized user or account manager on the Capital One Business credit card account and has an online Capital One login. In order to be granted travel admin role permissions, users have to be in this category.
      </li>
      <li>
        <strong>A user that only needs travel booking access. </strong>This type of user is a Non-Financial User and will be invited to create a Capital One Business Travel login that will have business travel access. This user will not be able to log into capitalone.com and will not be affiliated with the business credit card account beyond Capital One Business Travel.
      </li>
      <li>
        <strong>A user that won’t be booking their own travel. </strong>This type of user is solely a traveler profile and the user will not have access to log into Capital One Business Travel. Admins must book on behalf of this type of user through Capital One Business Travel where they can create a profile with a traveler’s name and relevant information (e.g., birthdate, gender, loyalty information).
      </li>
    </ul>
  </li>
</ul>
<p>
    User types are subject to change as we build out new features and capabilities, so please refer to the Capital One Business Travel Users tab for the latest and most accurate information.
</p>
<ul>
  <li>
    <strong>Capital One Business Expense Management Customers.</strong> Small business cardholders enrolled in Expense Management who book on Capital One Business Travel will automatically have purchase and booking receipts uploaded to Expense Management. Any Primary cardholder who does not wish to have Capital One Business Travel receipts automatically uploaded to Expense Management must un-enroll from Expense Management by logging into their Capital One account.
  </li>
</ul>

<h3 id="general-travel-information"><i>General Travel Information</i></h3>
<ul>
  <li>
    <strong>Required photo identification. </strong>Approved, government-issued photo identification
    is required for travel and must match the full name on the reservation. Due to the REAL ID
    requirements, your driver’s license or ID card may not be accepted for air travel after certain
    dates if it is not REAL ID. For the most current information on the REAL ID requirements, please
    visit: https://www.dhs.gov/real-id. Air carriers cannot board any passenger who fails to carry
    required documents. Some air carriers may also require you to show the credit card used as
    payment for your ticket(s).
  </li>
  <li>
    <strong>International travel requirements. </strong>If this is an international trip, you will
    need a passport, and you may need a visa. Travel to certain international destinations also may
    be subject to governmental health requirements. Passports usually need to be valid for a
    specified period of time (such as six months) after your scheduled return date. For foreign
    entry requirements, go to http://travel.state.gov or contact the embassy/consulate of the
    country to which you are traveling, including layover and stopover destinations, to determine
    entry documentation and other requirements, such as immunizations that you must satisfy,
    including return entry into the country from which you departed. It is solely your
    responsibility to obtain proper travel identification and satisfy all requirements for each
    location on your itinerary, including layover and stopover destinations. Capital One Business
    Travel is not responsible if you are refused entry onto a flight or into any country due to
    your failure to satisfy any entry requirements. Government imposed departure or entry taxes may
    not be included in ticket taxes. Passengers should be prepared to pay these taxes in cash, in
    local currency, on location. Requirements may change and you should check for up-to-date
    information before booking and departure.
  </li>
  <li>
    <strong>Minors. </strong>Minors under the age of 18 who are traveling with only one parent may
    be required to have additional documentation. Please contact your airline or the
    embassy/consulate of the country from which you are departing and the country to which you are
    traveling for additional information.
  </li>
  <li>
    <strong>Accommodations. </strong>Capital One Business Travel has no special knowledge
    regarding the suitability for persons with disabilities for any travel itinerary.
  </li>
  <li>
    <strong>Potential dangers. </strong>Capital One Business Travel has no special knowledge
    regarding unsafe conditions, health hazards, weather hazards, or climate extremes at locations
    to which you may travel. By offering reservations for travel products in particular
    destinations, Capital One Business Travel does not represent or warrant that travel to such
    areas is advisable or without risk, and is not liable for damages or losses that may result from
    travel to such destinations. For information concerning possible dangers at international
    destinations, Capital One Business Travel recommends contacting the Travel Advisory Section
    of the U.S. State Department at 202-647-5225 or checking online at
    travel.state.gov/traveladvisories.
  </li>
  <li>
    <strong>Public health. </strong>For travel-related public health information Capital One Business Travel
    recommends contacting the Centers for Disease Control and Prevention at
    800-232-4636. For foreign health requirements and dangers, go to http://www.cdc.gov/travel.
  </li>
  <li>
    <strong>Reservation rules. </strong>Travel reservations are subject to the rules of each
    Supplier on your itinerary. The information and descriptions given about the Suppliers are
    believed to be accurate, but Capital One Business Travel makes no warranty or representation
    regarding the information and descriptions.
  </li>
  <li>
    <strong>Tickets. </strong>The passenger's ticket(s), when issued, or the electronic reservation,
    shall constitute the sole contract between the Supplier and the purchaser and/or passenger.
    Capital One Business Travel shall have no liability for any actions or omissions of the
    Supplier, except where required by applicable law.
  </li>
  <li>
    <strong>Credits and vouchers. </strong>Capital One Business Travel shall have no
    responsibility for any credit or voucher issued by any Supplier, and any questions or issues you
    may have with respect to such credit or voucher must be addressed directly with the Supplier.
    Capital One Business Travel will only allow and facilitate usage of travel credits issued by
    airlines for bookings made through the Sites.
  </li>
  <li>
    <strong>Special requests and upgrades. </strong>Special requests made to a Supplier are on a
    request-only basis and cannot be guaranteed. Fees, taxes and charges may apply, depending on the
    service request. Upgrades are not permitted on certain itineraries. Please check with the
    Supplier directly. Supplier policies are subject to change at any time without notice.
  </li>
  <li>
    <strong>Loyalty programs. </strong>Certain rate types do not permit credit for airline frequent
    flyer programs or car or hotel loyalty programs. The name connected to the frequent flyer
    program or loyalty program must match the traveler listed on the itinerary or the Supplier may
    invalidate the booking.
  </li>
  <li>
    <strong>Luggage. </strong>Capital One Business Travel is not responsible for any lost,
    damaged, or delayed luggage.
  </li>
</ul>
<h3 id="links-to-other-internet-web-sites"><i>Links to Other Internet Web Sites</i></h3>
<p>
  Some of the web sites listed as links herein are not under the control of Capital One Business Travel
   but rather are exclusively controlled by third parties. You should review the terms
  applicable to any third-party site that you choose to access. Accordingly, Capital One Business Travel
  makes no representations whatsoever concerning such websites. Although Capital One Business Travel
  may provide a link to a third party, such a link is not an authorization,
  endorsement, sponsorship or affiliation by Capital One Business Travel with respect to such
  Web site, its owners or its providers. Capital One Business Travel is providing these links
  only as a convenience to you. Capital One Business Travel has not tested any information,
  products or software found on such web sites and therefore cannot make any representations
  whatsoever with respect thereto. It is up to you to take precautions to ensure that whatever you
  select is free of such items as viruses, worms, trojan horses and other items of a destructive
  nature.
</p>
<p>
  IN NO EVENT WILL Capital One Business Travel, CAPITAL ONE, OR ITS AFFILIATED COMPANIES, BE
  LIABLE TO ANY PARTY FOR ANY DIRECT, INDIRECT, SPECIAL OR OTHER CONSEQUENTIAL DAMAGES FOR ANY USE
  OF ANY HYPERLINKED WEBSITE, INCLUDING, WITHOUT LIMITATION, ANY LOST PROFITS, BUSINESS
  INTERRUPTION, LOSS OF PROGRAMS OR OTHER DATA ON YOUR INFORMATION HANDLING SYSTEM OR OTHERWISE,
  EVEN IF WE ARE EXPRESSLY ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
</p>
<h3 id="third-party-travel-services"><i>Third-Party Travel Services</i></h3>
<p>
  The purchase of third-party travel products and services through the Sites which may be offered
  separately or as part of a single checkout experience in “Hotels + Flights”, as explained further
  below, will be governed by and subject to additional terms and conditions from such providers, and
  subject to laws and regulations imposed by government entities, and you agree to abide by all such terms,
  laws, and regulations. Where possible, Capital One Business Travel will make these terms available to
  you. However, additional applicable terms may be provided on the third-party provider’s website,
  for example, an airline’s cancellation or refund policy. Please read these additional provider
  terms and conditions carefully prior to purchase, and ensure that you understand the full terms
  applicable to your airfare, hotel, trip insurance, car rental, or other third-party service. If
  you make bookings on behalf of another person(s) with their authorization, you agree that you will
  inform such other person(s) about these Terms and any third-party terms, rules and restrictions
  that apply to the bookings made on their behalf. You understand and agree that any violation of
  third-party terms of service may result in a cancellation of your bookings, denial of service, and
  forfeit of any amounts paid. Capital One Business Travel is not responsible for any damages
  resulting from your violation of third-party terms.
</p>
<h3 id="flight-bookings-terms-and-conditions"><i>Flight Bookings Terms and Conditions</i></h3>
<ul>
  <li>
    <strong>General ticket rules. </strong>Airlines are also interchangeably referred to as “air
    carriers” or “carriers” in these Terms and Conditions. Airline tickets are NON-REFUNDABLE and
    NON-CHANGEABLE unless permitted by airline fare rules and, if permitted, are subject to airline
    rules, airline penalties up to the full amount of each ticket plus, in the case of a changed
    ticket, fare difference charges, and may also be subject to Capital One Business Travel
    fees. Credit card purchases of flights through the Sites, as well as any such fees, will appear
    on your monthly card statement as "COT*FLT*BIZ". <strong>Note:</strong> If there is more than
    one carrier on your itinerary, airline rules, airline penalties and change fees may apply to
    EACH airline on your itinerary and each change made.
  </li>
  <li>
    <strong>Unused or canceled tickets. </strong>If the ticket is refundable, there may be
    cancellation fees to refund the ticket. Unused tickets may have no value if not canceled prior to
    the scheduled departure date/time as specified by the airline's fare rules. Failure to use any
    reservations may result in automatic cancellation of all continuing and return reservations. You
    must advise your carrier if your travel plans change en route. Tickets cannot be reassigned or
    transferred to a different passenger or airline.
  </li>
  <li>
    <strong>Additional services and fees. </strong>Airlines may impose additional costs and fees for
    baggage, meals, beverages and other services. These costs are your sole responsibility. For
    information regarding airline liability limitations, baggage liability and other regulations of
    the Montreal Convention, and other regulations, please consult your air carrier.
  </li>
  <li>
    <strong>Flight availability. </strong>A small number of air carriers may require Capital One
    Business Travel to confirm flight availability when booking. If there is any issue with
    availability, you will receive an email notification of the unavailability within 72 hours and
    will be refunded to your original payment method for any charges. Also, please see U.S.
    NOTICE—OVERBOOKING OF FLIGHTS below regarding flight availability after booking.
  </li>
  <li>
    <strong>Airline travel credits</strong><strong>. </strong>In some situations an airline may
    issue you a credit “in lieu of” a refund according to the fare rules associated with the
    individual booking. If a credit is issued by an airline, it is held by the airline in the name
    of the individual who was the passenger of record on the original reservation. This credit can
    be used towards the payment for the booking of a new trip, subject to the terms and conditions
    of the credit-issuing airline.
  </li>
  <li>
    <strong>Flight schedules. </strong>Flight schedules are subject to change. Capital One Business
    Travel may inform you of schedule changes in some scenarios, but is not responsible for
    any schedule change(s) or notifying you of such change(s). It is your responsibility to confirm
    the scheduled departure time(s) on the airlines' websites at least 48 hours prior to departure
    for domestic flights and at least 72 hours prior to departure for international flights to learn
    if your flight schedule changed.
  </li>
  <li>
    <strong>Check-in time and requirements. </strong>Check with each airline regarding its specific
    boarding and check-in requirements. We recommend that you check-in before arriving at the
    airport, where such advance check-in is offered by our airline. Some airlines offer advance
    check-in beginning 24 hours before departure. CHECK-IN TIME: The time shown on the
    itinerary/receipt is the departure time of the aircraft. Flight departure time is not the same
    as the time you must check-in or the time you must be available for boarding. Your carrier may
    refuse your carriage if you arrive after the aircraft has completed boarding or you are
    otherwise late. Check-in times, as advised by your carrier, are the latest times at which
    passengers can be accepted for travel; boarding times, as advised by your carrier, are the
    latest times at which passengers must present themselves for boarding. Flight cannot be held for
    late-arriving passengers, and Capital One Business Travel accepts no responsibility in such
    cases.
  </li>
  <li>
    <strong>Aircraft. </strong>Turboprop aircraft may exist on your itinerary. Airlines reserve the
    right to change aircraft equipment without notice to the booking travel agency or the consumer.
  </li>
  <li>
    <strong>Code-sharing. </strong>If a code-share flight exists in your itinerary (a flight where
    two or more airlines share the same flight), passengers must check in with the operating airline
    on the day of departure.
  </li>
  <li>
    <strong>Tickets and boarding passes. </strong>All tickets will be issued at the time of booking
    as e-tickets. You will receive a boarding pass upon checking in with the airline digitally or at
    the airport.
  </li>
  <li>
    <strong>Seat selection. </strong>Advance seat assignments, if available and allowed by airline,
    are not guaranteed and could be subject to additional fees. Please inquire with the airline.
  </li>
  <li>
    <strong>Insecticides. </strong>Please go to
    <a href="https://www.dot.gov/office-policy/aviation-policy/aircraft-disinsection-requirements"
      >https://www.dot.gov/office-policy/aviation-policy/aircraft-disinsection-requirements</a
    >
    to learn about the use of insecticides in certain aircraft.
  </li>
  <li>
    <strong>Large group bookings</strong><strong>.</strong> You may add up to 6 passengers onto a
    single booking. If you need to book for more than 6 passengers, you may do so for any remaining
    passengers via another booking. Each booking will have a maximum of 6 passengers. Please ensure
    that you do not duplicate passenger names across bookings with identical flights and dates. If
    multiple bookings are identical, including passenger names, they are considered duplicate
    bookings and will be subject to cancellation by the airline.
  </li>
  <li>
    <strong>Unaccompanied minors policy. </strong>Unfortunately, due to airline contracts, Capital
    One Business Travel cannot support online bookings in which the only traveler is less than
    18 years old. If you want to book a flight for an unaccompanied minor, please call Capital One
    Business Travel and book via phone (844-422-6922). The phone booking fee for an
    unaccompanied minor booking will be waived.
  </li>
  <li>
    <strong>Canadian traveler rights.</strong> For flights that are destined for, transit through,
    or depart Canada, passengers may be entitled to certain standards of treatment and compensation
    under the Air Passenger Protection Regulations. For more information about your passenger rights
    please contact your air carrier or visit the Canadian Transportation Agency’s website at
    <a href="https://www.otc-cta.gc.ca/">https://www.otc-cta.gc.ca/</a>.
  </li>
  <li>
    <strong>EU traveler rights.</strong> If you are traveling into or out of the European Union, or
    on an EU-based airline, You may have rights in the event that your flight is canceled, delayed
    or you are denied boarding. Please consult Regulation (EC) 261/2004 for more information.
  </li>
  <li>
    <strong>Hazardous materials. </strong>Federal law forbids the carriage of hazardous materials
    aboard aircraft in your luggage or on your person. A violation can result in five years'
    imprisonment and penalties of $250,000 or more (49 U.S.C. 5124). Hazardous materials include
    explosives, compressed gasses, flammable liquids and solids, oxidizers, poisons, corrosives and
    radioactive materials. Examples: Paints, lighter fluid, fireworks, tear gases, oxygen bottles,
    and radio-pharmaceuticals. There are special exceptions for small quantities (up to 70 ounces
    total) of medicinal and toilet articles carried in your luggage, spare lithium batteries in
    carry-on baggage, and certain smoking materials carried on your person. Certain items are
    required to be carried with you onboard the aircraft. Spare lithium batteries and cigarette
    lighters must be removed from checked or gate-checked baggage and carried onboard the aircraft.
  </li>
  <li>
    <strong>Baggage allowance.</strong> Excess valuation may be declared on certain types of
    articles. Carriers may apply special rules for fragile, valuable, or perishable articles. Check
    with your carrier. Carriers may permit a free checked baggage allowance, which is set by the
    carrier and may differ by class, and/or route. Carriers may apply extra charges for checked
    baggage in excess of their permitted allowance. Check with your carrier. Cabin (Unchecked)
    Baggage: Carriers may permit a free cabin baggage allowance, which is set by the carrier and may
    differ by class, route, and/or aircraft type. It is recommended that cabin baggage be kept to a
    minimum. Check with your carrier. If more than one carrier is providing the transportation for
    your journey, each carrier may apply different rules on baggage (both checked and cabin).
    SPECIAL BAGGAGE LIABILITY LIMITATIONS FOR U.S. TRAVEL: For domestic travel wholly between U.S.
    points, federal rules require any limit on a carrier’s baggage liability to be at least US
    $3,800 per passenger, or the amount currently mandated by 14 CFR § 254.5.
  </li>
  <li>
    <strong>Required information. </strong>The Transportation Security Administration (TSA), part of
    the U.S. Department of Homeland Security, requires Capital One Business Travel to collect
    information from you for purposes of watch list screening, pursuant to 49 U.S.C. Sec. 114, and
    the Intelligence Reform and Terrorism Prevention Act of 2004. TSA may share information you
    provide with law enforcement or intelligence agencies or others under its published system of
    records notice. For more information, please see <a href="https://www.tsa.gov">www.tsa.gov</a>.
  </li>
</ul>
<h4 id="advice-to-international-passengers">ADVICE TO INTERNATIONAL PASSENGERS ON LIMITATIONS OF LIABILITY</h4>
<p>
  PASSENGERS EMBARKING UPON A JOURNEY INVOLVING AN ULTIMATE DESTINATION OR A STOP IN A COUNTRY OTHER
  THAN THE COUNTRY OF DEPARTURE ARE ADVISED THAT THE PROVISIONS OF AN INTERNATIONAL TREATY (THE
  WARSAW CONVENTION, THE 1999 MONTREAL CONVENTION, OR OTHER TREATY), AS WELL AS A CARRIER’S OWN
  CONTRACT OF CARRIAGE OR TARIFF PROVISIONS, MAY BE APPLICABLE TO THEIR ENTIRE JOURNEY, INCLUDING
  ANY PORTION ENTIRELY WITHIN THE COUNTRIES OF DEPARTURE AND DESTINATION. THE APPLICABLE TREATY
  GOVERNS AND MAY LIMIT THE LIABILITY OF CARRIERS TO PASSENGERS FOR DEATH OR PERSONAL INJURY,
  DESTRUCTION OR LOSS OF, OR DAMAGE TO, BAGGAGE, AND FOR DELAY OF PASSENGERS AND BAGGAGE.
</p>
<p>
  ADDITIONAL PROTECTION CAN USUALLY BE OBTAINED BY PURCHASING INSURANCE FROM A PRIVATE COMPANY. SUCH
  INSURANCE IS NOT AFFECTED BY ANY LIMITATION OF THE CARRIER’S LIABILITY UNDER AN INTERNATIONAL
  TREATY. FOR FURTHER INFORMATION PLEASE CONSULT YOUR AIRLINE OR INSURANCE COMPANY REPRESENTATIVE.
</p>
<p>Notice of Contract Terms Incorporated by Reference</p>
<p>
  Your contract of carriage with the carrier that provides you with carriage by air, whether
  international, domestic or a domestic portion of an international journey is subject to this
  notice; to any notice or receipt of the carrier; and to the carrier’s individual terms and
  conditions (Conditions), related rules, regulations and policies (Regulations) and any applicable
  tariffs If your carriage is by more than one carrier, different Conditions, Regulations and any
  applicable tariffs may apply for each carrier. The Conditions, Regulations and any applicable
  tariffs of each carrier are, by this notice, incorporated by reference into and made part of your
  contract of carriage. The Conditions may include, but are not restricted to:
</p>
<p>
  (a) Conditions and limits on the carrier’s liability for the bodily injury or death of passengers;
  (b) Conditions and limits on the carrier’s liability for the loss of, damage to or delay of goods
  and baggage, including fragile or perishable goods; (c) rules for declaring a higher value for
  baggage and for paying any supplementary fee that may apply; (d) application of the carrier’s
  Conditions and limits of liability to the acts of the carrier’s agents, servants and
  representatives, including any person providing either equipment or services to the carrier; (e)
  claims restrictions, including time limits by which passengers must file claims or bring actions
  against the carrier; (f) rules about reconfirmations or reservations; check in times; the use,
  duration and validity of air transportation services; and the carrier’s right to refuse carriage;
  (g) rights of the carrier and limits on the carrier’s liability for delay or failure to perform a
  service, including schedule changes, substitution of alternative carriers or aircraft and
  re-routing, and, when required by applicable law, the obligation of the carrier to notify
  passengers of the identity of the operating carrier or substituted aircraft; and (h) rights of the
  carrier to refuse carriage to passengers who fail to comply with applicable laws or who fail to
  present all necessary travel documents.
</p>
<p>
  You can obtain additional information on items (a) through (h) above and about your contract of
  carriage (including how to request a copy thereof) at places where transportation on the carrier
  is sold. Many carriers also have this information on their websites. When required by applicable
  law, you have the right to inspect the full text of your contract of carriage at the carrier’s
  airport and sales offices, and upon request, to receive a copy by mail or other delivery service
  from each carrier free of charge. If a carrier sells air transportation services or checks baggage
  specifying carriage on another carrier, it does so only as an agent for the other carrier.
</p>
<p>
  Itinerary Changes/Cancelations. Itinerary changes or cancelations you request (i.e., voluntary
  refunds), if permitted under the airline fare rules applicable to your ticket, may be subject to
  airline-imposed fees in addition to any fare differential at re-ticketing. Such fees vary by
  airline. Your airline may permit a portion of the fare paid for an unused nonrefundable ticket to
  be applied toward the purchase of future travel. For many airlines, tickets not changed or
  canceled prior to departure have no remaining value.
</p>
<p>
  Tax Refunds. In case of a no-show or cancellation of an air booking, you may be entitled to a
  refund of government taxes and fees included in the price of the ticket purchased. Where you are
  eligible for a refund, you can request such a refund from Capital One Business Travel, who
  will submit your request to the airline on your behalf.
</p>
<p>
  Airline Prohibited Practices. The circumvention of an air carrier’s rules, including practices
  such as back-to-back ticketing (booking two or more tickets with overlapping travel dates in order
  to circumvent minimum stay requirements) and hidden-city, point beyond or throwaway ticketing
  (booking tickets that include segments you do not intend to use in order to circumvent an air
  carrier’s pricing structure), is prohibited by many air carriers. You agree that you will not use
  Capital One Business Travel to engage in these or similar activities if prohibited by an
  airline.
</p>
<p>
  CARRIERS RESERVE THE RIGHT TO REFUSE CARRIAGE TO ANY PERSON WHO HAS ACQUIRED A TICKET IN VIOLATION
  OF APPLICABLE LAW OR THE CARRIER’S TARIFFS, RULES OR REGULATIONS.
</p>
<p>
  YOU CANNOT TRAVEL IF YOU DO NOT HAVE ALL REQUIRED TRAVEL DOCUMENTS, SUCH AS PASSPORT AND VISA.
  PASSENGER SHALL ARRIVE AT AIRPORT BY TIME EARLY ENOUGH TO COMPLETE DEPARTURE PROCEDURES.
</p>
<p>
  GOVERNMENTS MAY REQUIRE YOUR CARRIER TO PROVIDE INFORMATION ON OR PERMIT ACCESS TO PASSENGER DATA,
  INCLUDING INFORMATION WE MAY BE REQUIRED TO COLLECT FROM YOU DURING THE RESERVATION PROCESS.
</p>
<p>
  U.S. NOTICE—OVERBOOKING OF FLIGHTS. Airline flights may be overbooked, and there is a slight
  chance that a seat will not be available on a flight for which a person has a confirmed
  reservation. If the flight is overbooked, no one will be denied a seat until airline personnel
  first ask for volunteers willing to give up their reservation in exchange for a payment of the
  airline’s choosing. If there are not enough volunteers the airline will deny boarding to other
  persons in accordance with its particular boarding priority. With few exceptions persons denied
  boarding involuntarily are entitled to compensation. The complete rules for the payment of
  compensation and each airline’s boarding priorities are available at all airport ticket counters
  and boarding locations. Some airlines do not apply these consumer protections to travel from some
  foreign countries although other consumer protections may be available. Check with the airline or
  your travel agent.
</p>
<h3 id="hotel-bookings-terms-and-conditions"><i>Hotel Bookings Terms and Conditions</i></h3>
<ul>
  <li>
    <strong>General hotel booking rules. </strong>Hotel rooms are NON-REFUNDABLE and NON-CHANGEABLE
    unless permitted by the terms of the room/rate description. Check the terms carefully before
    making a hotel reservation for cancelation or change terms and fees. For rooms that are
    non-refundable, if you change or cancel your reservation at any time, you will still be charged
    the full reservation amount of room and tax for the entire stay. Early check-out from a hotel is
    not subject to a refund, unless allowed by the Supplier. If permitted, cancelations or
    modifications received at any time may be subject to a Capital One Business Travel fee in
    addition to any Supplier fees. Credit card purchases of hotel rooms through the Sites, as well
    as any such fees, will appear on your monthly card statement as "COT*HTL*BIZ". Please note that
    reservations do not include services not specified in the reservation confirmation.
  </li>
  <li>
    <strong>Cancelations. </strong>Contact Capital One Business Travel via the number listed on
    your itinerary (844-422-6922) for all cancelation or change requests. Cancelations or changes
    handled by the hotel directly may result in additional fees and/or the forfeiture of any refund
    due. When canceling hotel reservations, retain your cancelation number so you will not be held
    responsible for cancelation charges if you cancel within the permitted cancelation window and
    the hotel makes an error.
  </li>
  <li>
    <strong>Non-transferability. </strong>Hotel room reservations cannot be transferred or
    reassigned to another traveler and cannot be resold. If we determine that you have transferred
    or re-sold a reservation, those reservations are subject to cancelation and will incur
    applicable cancelation fees in accordance with the rate description.
  </li>
  <li>
    <strong>No shows and lateness. </strong>No shows are non-refundable and will result in a
    forfeiture of all payments made and used by you in connection with the reservation, without
    credit due. If you think you may arrive at a hotel late, please contact the hotel directly to
    arrange for late arrival, if available. Actual times for "late" vary by hotel.
  </li>
  <li>
    <strong>Check-in requirements. </strong>The hotel will require a major credit card, in the name
    of one of the guests, or a cash deposit upon check-in. Government-issued photo identification is
    required at check-in and must match the name on the reservation. Some properties have a minimum
    age requirement for check-in. In some cases, your name may not be provided by Capital One Business
    Travel to the hotel until 24 hours prior to your arrival, due to hotel Supplier policies
    applicable to our preferred rates.
  </li>
  <li>
    <strong>Incidental charges. </strong>Hotel reservations include room and applicable hotel taxes
    only. Any additional hotel charges, such as resort fees and hotel energy surcharges and cleaning
    fees, and any charges for incidentals that you incur are not included in your reservation rate
    and must be paid directly to the hotel. Incidental charges may include but are not limited to
    parking fees, baby sitting, room service, telephone fees, internet usage fees, in-room movies,
    mini-bar charges, and gratuities.
  </li>
  <li>
    <strong>Special requests. </strong>Policies for children vary by hotel. Please contact the hotel
    directly to learn whether child benefits are offered and whether there are child restrictions.
    Additionally, please contact the hotel directly for any special requests, such as bed type,
    smoking preferences or in-room amenities. Special requests are subject to hotel availability.
  </li>
  <li>
    <strong>Potential renovations and refurbishments. </strong>A reasonable attempt will be made to
    notify guests of hotel renovation or refurbishment if Capital One Business Travel knows of
    the same; however, Capital One Business Travel shall not be liable for any failure to
    provide such notification or for damages that may result from renovation or refurbishment.
  </li>
  <li>
    <strong>Hotel ratings. </strong>Third-party hotel ratings displayed on the Sites are intended as
    only general guidelines, and Capital One Business Travel does not guarantee or endorse any
    hotel, or the accuracy of any ratings.
  </li>
  <li>
    <strong>Loyalty rates and hotel status. </strong>Capital One Business Travel may have hotel
    inventory shown on the Sites that have specially negotiated loyalty rates and discounts. Capital
    One Business Travel has no obligation or responsibility to provide or continue to provide
    loyalty rates and discounts. Any such loyalty rates and discounts are for the benefit of
    cardholders. In order to earn points towards status with a hotel chain, you must input the
    loyalty number associated with the hotel chain at the time of booking. Your name must match the
    name on the loyalty account for hotels to validate.
  </li>
  <li>
    <strong>Taxes. </strong>To the extent applicable, you acknowledge and agree that the supplier
    shall be treated by any tax authority in any pertinent jurisdiction as the supplier to the
    customer of e-collect bookings and the travel service component of an API partner package
    (where applicable) for VAT purposes as applied by Directive 2006/112/EC (where applicable) and
    any equivalent domestic legislation in any country.
  </li>
</ul>
<h3 id='package-terms'><i>“Hotels + Flights” Terms and Conditions</i></h3>
<p>Capital One Business Travel may offer you the opportunity to book a flight and hotel in a single checkout  using our “Hotels + Flights” feature. Other than as explicitly stated in this section, each third-party travel service is subject to these Capital One Business Travel Terms and Conditions, and any of its own rules and restrictions (including rules and restrictions regarding changes and cancellations of bookings) that may be communicated to you at the time of booking.</p>
<ul>
  <li>Flights and hotels purchased using the Hotels + Flights feature, are not eligible for any price protection (e.g. Price Match Guarantee, price freeze).</li>
  <li>Availability may vary. There may be certain flights, hotels, services or features available for purchase in connection with or as a standalone third-party travel service, which may not be available for booking or purchase as part of a single checkout in Hotels + Flights.</li>
</ul>

<h3 id="rental-car-bookings-terms-and-conditions"><i>Rental Car Bookings Terms and Conditions</i></h3>
<ul>
  <li>
    <strong>General rental car booking rules. </strong>Car rental redemptions may not be available
    for all locations and destinations. Cancellations or modifications received at any time are
    subject to the Supplier’s cancellation policies and cancellation fees which could be up to the
    full amount of the reservation and may also be subject to a Capital One Business Travel fee.
    Credit card purchases of rental cars through the Sites, as well as any such fees, will appear on
    your monthly card statement as COT*CAR*BIZ.
  </li>
  <li>
    <strong>No shows. </strong>No shows are non-refundable and will result in a total forfeiture of
    any payments made and used by you in connection with the reservation, without credit due.
  </li>
  <li>
    <strong>Early returns. </strong>The early return of car rentals is not eligible for a refund,
    regardless of situation or notification to the car rental company.
  </li>
  <li>
    <strong>Additional fees and surcharges. </strong>Any additional taxes, fees and surcharges are
    subject to change without notice, may vary by location, and may be charged to the customer at
    pick-up.
  </li>
  <li>
    <strong>Rental rates and rental periods. </strong>Rental rates are based on 24-hour periods and
    may be subject to additional fees depending on time of return, including but not limited to
    hourly rental charges, which will be billed directly to you by the car rental company. Certain
    car rentals may have a minimum rental period. Any rentals less than the required minimum days
    may be charged the minimum rental period.
  </li>
  <li>
    <strong>Domestic and international rates and considerations. </strong>Advance purchase car
    rental rates in the United States include unlimited mileage, taxes and fees. Car rental rates
    outside of the United States may not include unlimited mileage, taxes and fees and these will be
    assessed by the car rental company directly. Charges are billed directly by the car rental
    company and rates are subject to change. Rentals outside the country in which you reside may
    require an international driver’s license or compliance with other local requirements.
    Geographic and cross border restrictions may apply. Local renters and renters driving out of
    state/country may be subject to additional restrictions.
  </li>
  <li>
    <strong>Additional charges. </strong>Charges for optional services such as insurance, fuel,
    additional or underage drivers, and special equipment charges, are not included in your rental
    and must be paid directly to the car rental company. Car rental rates do not include Collision
    Damage Waiver insurance and Theft Protection unless specified. Please contact your insurance
    company if you are unsure whether to accept rental car company insurance at the counter.
  </li>
  <li>
    <strong>Vehicle. </strong>Capital One Business Travel does not guarantee a specific make,
    model, or color of vehicle no matter what vehicle is reserved.
  </li>
  <li>
    <strong>Age requirements. </strong>Renters must meet the minimum (and maximum, if applicable)
    age requirement where the car is being rented, have a valid driver's license, major credit card
    and some Suppliers require a good driving record. Most rental car companies do not accept debit
    cards. Some Suppliers charge a surcharge for drivers above or below certain ages, which is
    collected by the Supplier at the time of pickup. Suppliers reserve the right to deny car rentals
    for any reason, including past driving records.
  </li>
  <li>
    <strong>One way rentals. </strong>One way rentals may or may not be permitted. If there are
    additional fees for one way rentals, when permitted, these fees will not be included in the cost
    of the reservation. A reasonable attempt to advise of these fees will be made when this
    information is available to Capital One Business Travel.
  </li>
</ul>
<h3 id="vacation-rental-terms-and-conditions"><i>Vacation Rental Terms and Conditions</i></h3>
<p>Vacation rental bookings are only guaranteed once the purchase has been completed and a confirmation has been issued to you by Capital One Business Travel by email and/or through in-app messaging following your purchase.</p>
<ul>
  <li>
    <strong>Paid Bookings for Vacation Rentals.</strong> Capital One Business Travel and Capital One Travel LLC, directly and/or
    in partnership with other entities, have pre-negotiated certain rates with suppliers or property managers or
    their third party representatives (“Vacation Rental Providers”) to book vacation rentals. In connection with
    these services, Capital One Business Travel may charge a fee, which is combined with and
    included in the final rate that you see displayed in the Capital One Business Travel website and mobile application,
    and which may vary depending on the booking. This displayed rate may also include applicable taxes and third party
    service fees. When you make a prepaid booking (“Paid Booking(s)”) for vacation rentals, you authorize
    Capital One Business Travel to charge the full displayed amount on behalf of the Vacation Rentals Provider
    for the booking of the vacation rental(s), which may be charged to your payment instrument in a single
    transaction or multiple transactions as disclosed at the time of booking. Capital One Business Travel will
    facilitate communication regarding your reservation(s) with Vacation Rental Providers. Capital One Business
    Travel and its payment processor act as the agent of the Vacation Rental Providers in receiving payment for
    Paid Bookings, and receipt of your payment by Capital One Business Travel or
    its payment processor shall satisfy your payment obligations to the Vacation Rental Provider for such booking
    (except in the case of a chargeback or other payment reversal).
  </li>

  <li style="margin-top: 16px;">
    <strong>Cancellations.</strong> You may cancel or change your Paid Booking for vacation rentals,
    but you may be charged the cancellation or change fee on behalf of your Vacation Rental Provider
    indicated in the rules and restrictions for the applicable reservation, subject to applicable law.
    If you do not cancel or change your reservation before the cancellation policy period applicable
    to the property you reserved, which varies by Vacation Rental Provider, prior to your date of arrival,
    you may be subject to a charge on behalf of your Vacation Rental Provider equal to applicable nightly
    rates, tax recovery charges and service fees. In the event you do not show for the first night of the
    reservation and plan to check-in for subsequent nights in your reservation, you must confirm the
    reservation changes either with us or the Vacation Rental Provider, as applicable, and such changes
    must be pursuant to the rules and restrictions applicable to your reservation. Subject to applicable
    law, you agree to pay any cancellation or change fees that you incur. In limited cases, some Vacation
    Rental Providers do not permit changes to or cancellations of reservations after they are made, as
    indicated in the rules and restrictions for the reservation. You agree to abide by the applicable
    terms of use imposed with respect to your Paid Booking for vacation rentals. This may include losing
    any deposit paid by you for your Paid Booking.
  </li>
  <li style="margin-top: 16px;">
    <strong>Incidental and Other Charges by Property Managers or Suppliers. </strong> Some Vacation Rental
    Providers may require you to pre-authorize a deposit to be charged to your credit card,
    or require a cash deposit upon check-in to cover additional expenses incurred during your
    stay. Such deposit is separate and unrelated to any payment received by Capital One Business
    Travel on behalf of your Vacation Rental Provider for your vacation rental booking.
    You are responsible for payment of all additional expenses during your stay, including for example,
    housekeeping fees, resort fees, etc.
  </li>
  <li style="margin-top: 16px;">
    <strong>Guest Responsibilities / Assumption of Risk. </strong> You are responsible and liable for
    your own acts and omissions and are also responsible for the acts and omissions of anyone you invite
    to join or provide access to any vacation rental, all areas and facilities where the vacation rental
    is located that the Vacation Rental Provider and guest are legally entitled to use in connection
    with the vacation rental (“Common Areas”), or any other service. For example, this means: (i) you
    are responsible for leaving a vacation rental (and related personal property) or Common Areas in
    the general condition it was in when you arrived, (ii) you are responsible for paying all reasonable
    damage amounts, and (iii) you must act with integrity, treat others with respect, and comply with applicable
    laws at all times. If you are booking for an additional guest who is a minor, or if you bring a minor to a vacation
    rental, you must be legally authorized to act on behalf of the minor and you are solely responsible for the supervision
    of that minor. You acknowledge that many activities carry inherent risks and agree that, to the maximum
    extent permitted by applicable law, you assume the entire risk arising out of your access to and
    use of the Capital One Business Travel website and mobile application and any content,
    including your stay at any vacation rental, or any other interaction you have with other
    Vacation Rental Providers whether in person or online. This means it is your responsibility
    to investigate a vacation rental to determine whether it is suitable for you.
  </li>
  <li style="margin-top: 16px;">
    <strong>Rules and Restrictions; Property Damage. </strong> Each vacation rental has specific
    restrictions and requirements set forth by the Vacation Rental Provider. You are responsible
    for complying with all terms of the vacation rental booking including, without limitation, the
    cancellation policy and any other rules, standards, policies, or requirements identified in
    the listing, during the checkout process, or otherwise communicated to you by Capital One Business
    Travel or the Vacation Rental Provider prior to your stay. This may include restrictions on age,
    guests, pets, etc. Further, you acknowledge that if a valid claim is made, you may be liable for
    any damage you, your guests, or pets (if applicable) have caused to the vacation rental during
    your booking. You agree that you have proper authority to enter into and accept any Vacation
    Rental Provider’s applicable terms, damage policy, or cancellation policy on behalf of yourself
    or your guests. In the event you are found to violate: (i) these Terms, (ii) any rules or
    restrictions as set forth by a Vacation Rental Provider, or (iii) any applicable law, the
    Vacation Rental Provider, or Capital One Business Travel, in its sole discretion or on behalf
    of a Vacation Rental Provider, may cancel your vacation rental booking. This may include
    terminating your booking without any refund.
  </li>
  <li style="margin-top: 16px;">
    <strong>Ratings. </strong> Vacation rental ratings displayed in the Capital One Business
    Travel website and mobile application are intended as only general guidelines, and
    Capital One Business Travel does not guarantee or endorse any vacation rental, property
    manager, or the accuracy of any ratings.
  </li>
  <li style="margin-top: 16px;">
    <strong>Taxes and Fees. </strong> In connection with facilitating your vacation rental
    booking transaction, Capital One Business Travel may charge your method of payment for
    taxes and fees. This charge includes an anticipated amount to recover the amount Capital
    One Business Travel provides to the Vacation Rental Provider in connection with your
    reservation for taxes owed by the Vacation Rental Provider, or which Capital One
    Business Travel remits to a taxing authority on behalf of a Vacation Rental Provider,
    including, without limitation, sales and use tax, occupancy tax, room tax, excise tax,
    value added tax and/or other similar taxes. In certain locations, the tax amount may
    also include government imposed service fees or other fees not paid directly to the
    taxing authorities but required by law to be collected by the Vacation Rental Provider,
    or by Capital One Business Travel on behalf of the Vacation Rental Provider. Capital
    One Business Travel may not always be the entity collecting and remitting taxes to the
    applicable taxing authorities. The balance of the charge for service fees is a fee
    Capital One Business Travel retains as part of the compensation for the services.
  </li>
  <li style="margin-top: 16px;">
    <strong>Third-Party Terms of Service. </strong> In order to secure your Capital One Business
    Travel vacation rental reservation, we may be required to provide your information to
    third-party vacation rental supply aggregators and/or technology or connectivity providers
    in order to facilitate the reservation. By providing your information, You acknowledge that
    Capital One Business Travel is not responsible for any third-party provider terms and
    conditions or privacy policy, or any information or content provided for any third-party
    provider’s terms and conditions or privacy policy.
  </li>
  <li style="margin-top: 16px;">
    <strong>Electronic Communications. </strong> In connection with your Capital One Business
    Travel vacation rental booking, we may share information and communicate with third-party
    Vacation Rental Providers, including property managers, in order to enable such service
    providers to fulfill a reservation. As a result, third-party service providers, including
    property managers, may contact you directly to facilitate your booking. You hereby consent
    to receive such electronic communications from third party service providers in connection
    with your Capital One Business Travel vacation rental reservation. Capital One Business
    Travel is not responsible for the content of such third-party communications including,
    but not limited to, marketing or promotional materials sent directly to you from a third
    party service provider, including property managers. You further acknowledge that we may
    contact third party service providers in order to satisfy your reservation and subsequently
    contact you via electronic communications to complete your booking.
  </li>
</ul>
<h2 id="terms-for-additional-products-and-features"><u>Terms for Additional Products and Features</u></h2>
<p>
  If offered, the additional products and features made available through Capital One Business Travel
  are subject to the following Terms and Conditions. Availability of these services may
  vary based on your account, and not all services are available to all customers or on all
  bookings. The availability of the services is subject to change without prior notice. Additional
  fees may apply. Capital One Business Travel may revise these Terms and Conditions at any time
  by updating this posting.
</p>
<p>
  Some of the services made available through Capital One Business Travel are offered by Hopper
  (USA) Inc. and its affiliated companies (“Hopper”). Your use of the services offered by Hopper,
  and other third-parties that may be linked to from the Sites will be governed by their respective
  terms and conditions and privacy policies.
</p>
<h3 id="price-prediction-and-alerts"><i>Price Prediction and Alerts</i></h3>
<p>
  Capital One Business Travel’s price prediction services and notifications provide a powerful tool to assist you in finding the best time to book
  travel and save money. Capital One Business Travel works hard to ensure the accuracy
  of its predictions based on past data and current trends. However, forecasting price movements in
  a highly competitive, unpredictable global market has inherent limitations, and no prediction tool
  can be 100% accurate. Accordingly, while Capital One Business Travel believes
  strongly in the accuracy of its predictions, they do not guarantee its accuracy, and shall not be
  liable for any reliance upon them, except as expressly set forth in the terms of any Capital One
  Business Travel product purchased by you, such as a price freeze.
</p>
<p>
  Disclaimer: Availability of these services may vary based on your account, and not all services
  are available to all customers or on all bookings. The availability of the services is subject to
  change without prior notice. Capital One Business Travel may revise these Terms and Conditions
  at any time by updating this posting.
</p>
<p>
  Some of the services made available through Capital One Business Travel are offered by Hopper
  (USA) Inc. and its affiliated companies (“Hopper”). Your use of the services offered by Hopper,
  and other third-parties that may be linked to from the Sites will be governed by their respective
  Terms and Conditions and Privacy Policies.
</p>

<h3 id="cancel-for-any-reason-flights"><i>Cancel for Any Reason (Flights)</i></h3>
${FLIGHT_CANCEL_FOR_ANY_REASON_BODY}

<h3 id="cancel-for-any-reason-hotels"><i>${HOTEL_CFAR_TITLE}</i></h3>
${HOTEL_CFAR_BODY}
${HOTEL_CFAR_TABLE}

<h3 id="flight-disruption-assistance"><i>Flight Disruption Assistance (formerly known as Rapid Rebooking)</i></h3>
${CORP_DISRUPTION_PROTECTION_BODY}

<h3 id="flight-disruption-rebooking"><i>Flight Disruption Rebooking</i></h3>
${CORP_DISRUPTION_REBOOKING_BODY}

<h3 id="price-match-guarantee"><i>Price Match Guarantee</i></h3>
<p>
  Most flight, hotel, and rental car purchases made through Capital One Business Travel are
  eligible for price matches if you find a cheaper itinerary elsewhere and let us know within 24
  hours of booking through Capital One Business Travel. As described above, flights and hotels
  booked using the “Hotels + Flights” feature are not eligible for
  Price Match Guarantee. Availability of this service may vary, and not all services are available
  to all customers or on all bookings. Capital One Business Travel may revise these Terms and Conditions
  at any time by updating this posting. If you select the option to ‘pay at stay’ when booking a Premier
  or Lifestyle Collection hotel, the booking is not eligible for Price Match Guarantee.
</p>
<ol>
  <li>
    <strong>Qualification criteria.</strong> To qualify for Capital One Business Travel’s price
    match guarantee, you must keep your original itinerary and the cheaper itinerary found must be:
    <ol type="a">
      <li>
        An exact match to the original itinerary that was made within the last 24 hours (including
        the cancellation policy, i.e. refundable or non-refundable);
      </li>
      <li>Priced in USD, including any taxes and fees;</li>
      <li>
        Available to the general public. For example, this means that the rate doesn’t require the
        customer to log into a site or have a special membership;
      </li>
      <li>
        Available at the time the customer contacts us. Our agent must be able to verify that the
        lower price exists.
      </li>
    </ol>
  </li>
  <li>
    <p>
      <strong>Submitting a claim.</strong> You must submit a price match guarantee claim through an
      agent by calling Capital One Business Travel at 833-233-5660. Please provide the trip ID
      for the original booking, the amount of the lower price, and the website where you found the
      lower price so that our agents can verify the lower price. If the claim is verifiable, our
      agents will authorize the refund live on the phone.
    </p>
    <p>
      If our agents are unable to verify the lower price, we will verify offline and will attempt to
      respond within 24 hours, though it may take longer in certain cases. If the claim is
      verifiable, you will receive an email confirmation of your refund. If the claim is not
      verifiable after an offline review, you will be notified of the decline by email, and you may
      not receive a refund.
    </p>
  </li>
  <li>
    <p>
    <strong>Refund method and amount.</strong> For reservations that meet all price match guarantee criteria,
    and subject to Section 5 (Forfeiture of Capital One Business Travel Credits) below, we will refund you 100%
    of the difference in price, minus taxes and fees, in the form of Capital One Business Travel Credits).
    Receipt and use of Capital One Business Travel Credits provided by Capital One are subject to certain terms
    and conditions - see the “Travel Credits” section in these terms and conditions for more information.
    If you used any Capital One Business Travel offer to purchase any amount of your flight, you will not be
    refunded in any form for such an amount, as further described above in these terms and conditions.
    </p>
  </li>
  <li>
    <p>
      <strong>Refund timing and rewards adjustments.</strong> Refunds should appear on your account
      within 1-2 billing cycles. Please be aware that the portion of the transaction that is
      refunded will result in an adjustment of any rewards earned on that transaction.
    </p>
  </li>
  <li>
    <p>
      <strong>Forfeiture of Capital One Business Travel Credits.</strong> If you make a booking through
      Capital One Business Travel and then find a cheaper itinerary elsewhere and receive
      Capital One Business Travel Credits as a price match guarantee, you must keep your
      original itinerary to be eligible for the price match guarantee Capital One Business Travel Credits.
      If you cancel your original itinerary after receiving a price match guarantee, any remaining
      unused Capital One Business Travel Credits you received from that price match guarantee will be voided
      and will no longer be available for you to use. In the event that the associated credits were already spent,
      we reserve the right to deduct the value of any spent credits from the refunded amount for the canceled itinerary.
    </p>
  </li>
</ol>

<h3 id="travel-credits"><i>Travel Credits</i></h3>
${CORP_TRAVEL_CREDITS_BODY}

<h3 id="intra-business-referral-terms"><i>Intra-Business Referrals</i></h3>
<p>
  <strong>Referral Credits.</strong> Capital One Business Travel may provide an intra-business referral offer in which a
  primary cardholder can refer employees within their business to book travel on Capital One Business Travel (“Offer”).
  This Offer is available only to select Capital One Business Travel Primary Cardholders and employees within their
  business (“Referred Employee(s)”). Capital One reserves the right to determine the Offer eligibility at its sole discretion.
</p>

<p>
  A Referred Employee will receive a discount of  $${offerAmount} off of any eligible booking in Capital One Business Travel,
  during the advertised offer period. When a Referred Employee makes an eligible booking, the Primary Cardholder
  for the business will receive $${offerAmount} worth of travel credits to be redeemed on future bookings within Capital One Business Travel.
</p>

<p>
  <strong>Referred Employees.</strong> Referred Employee(s) will receive an email notification once
  submitted by a Primary Cardholder. They will also see a $${offerAmount} off first booking offer displayed under
  their "My Travel Credits and Offers" page, accessible by clicking on the "My Travel Credits and Offers"
  icon in the navigation bar. This Offer is applicable on any booking a Referred Employee places within
  Capital One Business Travel, during the advertised offer period.
</p>

<p>
  If the Referred Employee forgets to apply the $${offerAmount} off first booking offer at the time of checkout,
  Primary cardholders will remain eligible to receive $${offerAmount} worth of travel credits.
</p>

<p>
  This Offer is for one time use per Referred Employee, per eligible business. Therefore, if the Referred Employee
  books with the $${offerAmount} off of the first booking offer and then cancels the booking, this Offer will no longer be valid.
</p>

<p>Please refer to the Travel Offers section above for more information. </p>

<p>
  <strong>Primary Cardholders.</strong> Available travel credits accrued from Referred Employee first time bookings in
  Capital One Business Travel, will be displayed in the “My Travel Credits and Offers” page, accessible by clicking on
  the “My Travel Credits and Offers” icon in the navigation bar. Primary Cardholder will be able to see which Referred
  Employee placed a booking via the credit name under "Travel credit activity".
</p>

<p>
  Travel Credits redeemed from this Offer will not expire for the life of the account, however they are not transferrable
  and are forfeited if the Primary Cardholder closes the applicable Capital One credit card. Please refer to the Travel Credits
  section above for more information.
</p>

<p>
<strong>Additional Rules and Restrictions.</strong>
  <ol style="list-style: circle; margin-left: 24px;">
    <li>Capital One Business Travel reserves the rights to extend this Offer and any provisions of this Offer to eligible businesses at random.</li>
    <li>Capital One Business Travel reserves the rights to end this Offer at any time for any reason.</li>
    <li>
      There is a limit of five (5) Offer redemptions per business account. Referred Employee must be current employees within the business
      associated with the Primary Cardholder’s Capital One Credit Card. Limit of one Offer per first time Capital One Business Travel users
    </li>
    <li>
      By participating in this Offer, you agree to these Terms and any other Capital One Business Travel terms and conditions applicable
      to your account and travel bookings.
    </li>
  </ol>
</p>

<h3 id="premier-collection-stays"><i>Premier Collection Stays</i></h3>
<p>
  <strong>Eligibility.</strong> To qualify for the Guaranteed Benefits and the Benefits Subject
  to Availability described below, Capital One Business Travel Premier Collection bookings must
  be made through Capital One Business Travel’s booking site, in the Venture X Business cardholder’s
  name, and that cardholder must be a member of the party traveling under the booking. For hotels,
  benefits are applied to each room booked under an eligible stay; a four-room limit per stay applies.
  Back-to-back bookings at the same property (less than 24-hours separating reservations) are
  considered one stay. Hotel terms and conditions apply and a minimum number of nights may be
  required by the hotel for booking. Participating properties and benefits are subject to change.
  Offer valid for consumer, non-commercial use only, unless otherwise permitted.
  We reserve the right to disqualify any cardholder from participation in this offer in
  the event of fraud, abuse, or a violation of the terms as determined at our discretion.
  Such termination may result in the forfeiture of any benefits.
  </p>
  <p>
  For vacation rentals, Premier Collection booking must be made through Capital One Business
  Travel in the eligible cardholder’s name, with the cardholder as a member of party traveling,
  to qualify for benefits. Benefits are applied per stay. Back-to-back bookings of the same property
  within a 24-hour period will be considered one reservation and are not eligible for double benefits.
  Host provider terms and conditions apply; a minimum number of nights may be required by the host for booking.
  Benefits cannot be redeemed for cash or combined with other offers unless specified. Participating properties
  and benefits are subject to change. Offer not intended for commercial use unless otherwise permitted. We may
  disqualify any cardholder from participation and benefits at our sole discretion.
</p>

<p>
  <strong>Guaranteed Benefits.</strong> Eligible cardholders will have the following benefits available
  to them for Premier Collection hotel bookings: (1) complimentary daily breakfast for two people,
  (2) complimentary Wi-Fi access, (3) 10 miles per dollar spent for eligible net purchases
  (purchases minus any credits or returns) of Premier Collection bookings made through Capital One Business Travel’s
  booking site, and (4) a hotel experience credit of $100. 'Pay at stay' bookings are excluded from elevated earn.
  Purchases made outside of Capital One Business Travel’s booking site, including but not limited to hotel
  incidentals, upgrades, or other expenses, will not earn the elevated rewards, but will receive the
  standard purchase earn rate. The hotel experience credit will be applied to qualifying charges at
  checkout by the hotel for up to $100 USD or the local currency equivalent. Qualifying charges vary
  by location and must be charged or applied to your room during your eligible stay (i.e., expenses or
  incidentals paid for directly with a card or other payment method at the time of service or purchase,
  rather than applied to your room, will not be eligible for the experience credit at checkout).
  Contact the hotel directly or inquire at check-in to confirm qualifying experiences and expenses.
  Advance reservations for certain qualifying experiences (e.g., spa) are recommended.
  Amenities vary by property. Benefits cannot be redeemed for cash, must be used during the
  eligible stay booked, and may not be combined with other offers unless specified.
  </p>
  <p>
  Eligible cardholders will have the following benefits available to them for Premier Collection vacation rental bookings:
  (1) 5 miles per dollar spent for net purchases (purchases minus any credits or returns) of Premier Collection
  bookings made through Capital One Business Travel’s booking site, (2) a vacation rental experience credit of
  $100 USD. Purchases made outside of Capital One Business Travel’s booking site will not earn elevated rewards,
  but will receive the standard purchase earn rate. The Premier Collection vacation rental experience credit
  will be applied at time of booking the experience by the host provider to qualifying experience charges up
  to $100 USD or the local currency equivalent. Experiences must be booked with a Capital One credit card.
  Qualifying experience credit options vary by location and are subject to availability. Contact the host
  provider directly to confirm qualifying experiences - advance reservations recommended for certain experiences.
  For certain properties, the experience credit will be provided in the form of a gift card.
</p>

<p>
  <strong>Benefits Subject to Availability.</strong> Eligible cardholders may have the following
  benefits available to them for Premier Collection hotel bookings if they are available:
  (1) early check-in, (2) late check-out, and (3) room upgrades. Certain room categories may not
  be eligible for upgrade. All other benefits not specifically listed are subject to availability.
  Benefits cannot be redeemed for cash, must be used during the eligible stay booked,
  and may not be combined with other offers unless specified.
</p>

<h3 id="lifestyle-collection-stays"><i>Lifestyle Collection Stays</i></h3>

<p>
<strong>Eligibility.</strong> Eligible products for Lifestyle Collection access include, but are not
limited to, Venture X Business, Spark Miles and Venture Business. To qualify for the Guaranteed Benefits and the Benefits
Subject to Availability described below, Capital One Business Travel Lifestyle Collection bookings must
be made through Capital One Business Travel’s booking site, in the eligible cardholder’s name, and that
cardholder must be a member of the party traveling under the booking. For hotels, Benefits are applied to
each room booked under an eligible stay; a four-room limit per stay applies. Back-to-back bookings at the same
property (less than 24-hours separating reservations) are considered one stay. Hotel terms and conditions
apply and a minimum number of nights may be required by the hotel for booking. Participating properties
and benefits are subject to change. Offer valid for consumer, non-commercial use only, unless otherwise
permitted. We reserve the right to disqualify any cardholder from participation in this offer in the event
of fraud, abuse, or a violation of the terms as determined at our discretion. Such termination may result
in the forfeiture of any benefits.
</p>

<p>
For vacation rentals, Lifestyle Collection booking must be made through Capital One Business
Travel in the eligible cardholder’s name, with the cardholder as a member of party traveling,
to qualify for benefits. Benefits are applied per stay. Back-to-back bookings of the same
property within a 24-hour period will be considered one reservation and are not eligible
for double benefits. Host provider terms and conditions apply; a minimum number of nights
may be required by the host for booking. Benefits cannot be redeemed for cash or combined
with other offers unless specified. Participating properties and benefits are subject to change.
Offer not intended for commercial use unless otherwise permitted. We may disqualify any cardholder
from participation and benefits at our sole discretion
</p>

<p>
<strong>Guaranteed Benefits.</strong> Eligible products for Lifestyle Collection access include, but are not limited
to, Venture X Business, Spark Miles and Venture Business. Eligible cardholders will have the following benefits available
to them for Lifestyle Collection hotel bookings: (1) 10 miles per dollar spent with a Venture X Business card
and 5 miles per dollar spent with a Spark Miles or Venture Business card for eligible net
purchases (purchases minus any credits or returns) of Lifestyle Collection bookings made through
Capital One Business Travel’s booking site, and (2) a hotel experience credit of $50. 'Pay at stay’
bookings are excluded from elevated earn. Purchases made outside of Capital One Business Travel’s
booking site, including but not limited to hotel incidentals, upgrades, or other expenses, will
not earn the elevated rewards, but will receive the standard purchase earn rate. The hotel experience
credit will be applied to qualifying charges at checkout by the hotel for up to $50 USD or the local
currency equivalent. Qualifying charges vary by location and must be charged or applied to your room
during your eligible stay (i.e., expenses or incidentals paid for directly with a card or other
payment method at the time of service or purchase, rather than applied to your room, will not be
eligible for the experience credit at checkout). Contact the hotel directly or inquire at check-in
to confirm qualifying experiences and expenses. Advance reservations for certain qualifying experiences
(e.g., spa) are recommended. Amenities vary by property. Benefits cannot be redeemed for cash, must
be used during the eligible stay booked, and may not be combined with other offers unless specified.
</p>
<p>
Eligible cardholders will have the following benefits available to them for Lifestyle Collection
vacation rental bookings: (1) 5 miles per dollar spent for net purchases (purchases minus any
credits or returns) of Lifestyle Collection bookings made through Capital One Business Travel’s booking
site, (2) a vacation rental experience credit of $50 USD. Purchases made outside of Capital One Business
Travel’s booking site will not earn elevated rewards, but will receive the standard purchase earn rate.
The Lifestyle Collection vacation rental experience credit will be applied at time of booking the
experience by the host provider to qualifying experience charges up to $50 USD or the local currency
equivalent. Experiences must be booked with a Capital One credit card. Qualifying experience credit
options vary by location and are subject to availability. Contact the host provider directly to confirm
qualifying experiences - advance reservations recommended for certain experiences. For certain properties,
the experience credit will be provided in the form of a gift card.
</p>

<p>
<strong>Benefits Subject to Availability.</strong> Eligible cardholders may have the following
benefits available to them for Lifestyle Collection hotel bookings if they are available:
(1) early check-in, (2) late check-out, and (3) room upgrades. Certain room categories may not
be eligible for upgrade. All other benefits not specifically listed are subject to availability.
Benefits cannot be redeemed for cash, must be used during the eligible stay booked, and may not
be combined with other offers unless specified.
</p>

<h3 id="complimentary-night-offers"><i>Complimentary Night Offers</i></h3>
<p>
Stay at eligible Premier Collection and Lifestyle Collection 
hotel properties for at least four consecutive nights and receive the fourth night complimentary. 
This offer is only valid for reservations of at least four nights made within the indicated 
offer dates (blackout dates may apply). This offer cannot be retroactively applied to bookings previously made. 
If the eligible reservation is subsequently shortened, the discount may no longer be applicable. 
Special offer is not combinable with other offers unless indicated, including, without limitation, 
on lower rate offers. In certain circumstances, this offer may not be available for all dates in your 
booking window; if you don’t see the offer with the property during the booking process, then it’s no longer available. 
If eligible, any complimentary nights will be reflected at the time of booking and applicable taxes and fees 
for the complimentary night will be removed at check-out. Savings displayed are based on offer rates 
provided by the property and may vary slightly. In some cases, this offer may be repeatable based on duration of stay. 
This will be dependent on the individual property and will be reflected at check-out if applicable.
</p>

<p>
Your Capital One account must be open and in good standing to be eligible for the offer. 
Travel offers are valid for consumer, non-commercial use only. Travel offers are non-transferrable, 
have no cash value, may not be redeemed for cash, and may not be combined with other offers unless specified. 
We reserve the rights to: alter or terminate a travel offer at any time; change these terms and conditions 
associated with travel offers; and/or disqualify any account holder from any travel offers.
</p>

<h2 id="website-terms-of-use">Website terms of use</h2>
${WEBSITE_TERMS_OF_USE_BODY}
`;
